import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, Sparkles, Telescope, Target } from "lucide-react";
import { SERVICES, METRICS, PARTNERS, INDUSTRIES } from "@/data/content";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/371a705d-1e9e-4b80-8780-1f565747a945/images/c5032847b278a2bb23ffa25ee713faaedfc057a31ce7ee29c2acdb0aa9356abc.png";
const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/371a705d-1e9e-4b80-8780-1f565747a945/images/0ad6762dbbc3a822051f5e6041c055abf52c2e644b20291cc1b84247fe5e4099.png";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Notion Minds Systems — Strategy, Security & Delivery for the GCC"
        description="Notion Minds Systems helps enterprises across Saudi Arabia, Oman and Qatar turn technology ambition into measurable outcomes — AI strategy, IT consulting, cybersecurity, OT, ODC, quality engineering and GIS."
        canonical="https://notionminds.sa/"
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <div className="absolute inset-0">
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-65"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        </div>

        <div className="relative w-full max-w-[1440px] mx-auto px-6 lg:px-12 pb-20 pt-32">
          <motion.div
            initial="initial"
            animate="animate"
            transition={{ staggerChildren: 0.15 }}
            className="max-w-4xl"
          >
            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.8 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.02] tracking-tight"
            >
              Strategy.<br />
              Security.<br />
              <span className="text-gradient-gold font-semibold">Delivery at scale.</span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.8 }}
              className="mt-8 max-w-2xl text-lg lg:text-xl text-white/80 leading-relaxed"
            >
              Enabling Saudi Arabia's digital transformation through AI,
              cybersecurity, cloud and engineering excellence.
            </motion.p>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.8 }}
              className="mt-5 max-w-2xl text-base lg:text-lg text-white/60 leading-relaxed"
            >
              Notion Minds Systems helps enterprises and transformation programs
              turn technology ambition into measurable business outcomes — from
              strategy and architecture through implementation and managed
              delivery.
            </motion.p>

            <motion.div variants={fadeUp} transition={{ duration: 0.7 }} className="mt-12 flex items-center gap-2">
              <span className="w-10 h-px bg-[#d4af37]" />
              <span className="font-mono-accent text-xs text-[#d4af37]">
                Saudi Arabia · Oman · Qatar
              </span>
            </motion.div>

            <motion.div variants={fadeUp} transition={{ duration: 0.8 }} className="mt-4 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 bg-[#d4af37] text-black px-7 py-4 font-semibold hover:bg-[#e5c059] transition-all"
              >
                Start a conversation
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-4 font-medium hover:border-white/60 hover:bg-white/5 transition-all"
              >
                Explore services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Metrics rail */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-white/10"
          >
            {METRICS.map((m, i) => (
              <div
                key={m.label}
                data-testid={`hero-metric-${i}`}
                className={`py-6 px-4 ${i !== 0 ? "md:border-l border-white/10" : ""}`}
              >
              <div className={`font-display font-semibold text-[#d4af37] ${m.value.length > 4 ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"}`}>
                {m.value}
              </div>
                <div className="text-xs text-white/50 mt-2 leading-snug">{m.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Vision 2030 banner */}
      <section className="border-t border-white/10 bg-gradient-to-b from-[#080810] to-[#050505] py-20 lg:py-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            data-testid="vision-2030-banner"
            className="relative overflow-hidden border border-[#d4af37]/25 grid-pattern-fine"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/8 via-transparent to-transparent pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 p-10 lg:p-14 items-center">
              <div className="lg:col-span-2 flex items-center gap-3">
                <span className="w-10 h-px bg-[#d4af37]" />
                <span className="font-mono-accent text-[10px] text-[#d4af37]">2030</span>
              </div>
              <div className="lg:col-span-10">
                <h2 className="font-display text-3xl lg:text-5xl font-light leading-tight mb-5">
                  Supporting <span className="text-gradient-gold font-semibold">Saudi Vision 2030</span>.
                </h2>
                <p className="text-white/70 text-base lg:text-lg leading-relaxed max-w-3xl">
                  Technology and engineering capabilities supporting digital
                  transformation, innovation, cybersecurity and enterprise
                  modernization across the Kingdom.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partners marquee */}
      <section className="border-y border-white/10 py-8 bg-[#080808]">
        <p className="font-mono-accent text-[10px] text-white/40 text-center mb-6">
          Trusted ecosystem · Specialist network
        </p>
        <Marquee gradient gradientColor="#080808" speed={35} pauseOnHover>
          {PARTNERS.map((p) => (
            <div
              key={p}
              className="mx-10 font-display text-2xl text-white/40 hover:text-white transition-colors"
            >
              {p}
            </div>
          ))}
        </Marquee>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 lg:py-32 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">
              [ 01 ] Practice areas
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-light max-w-3xl">
              Seven pillars built for <span className="text-gradient-gold font-semibold">regulated enterprises in Saudi Arabia, Oman & Qatar</span>.
            </h2>
          </div>
          <Link
            to="/services"
            data-testid="services-section-cta"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#d4af37] transition-colors group"
          >
            View detailed services
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className={`${s.span} group relative bg-[#0d0d12] border border-[#24242a] hover:border-[#d4af37]/60 p-8 lg:p-10 transition-all duration-500 overflow-hidden`}
                data-testid={`service-card-${s.id}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${s.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-8">
                    <div className="w-12 h-12 border border-white/15 group-hover:border-[#d4af37] flex items-center justify-center transition-colors">
                      <Icon className="w-5 h-5 text-white group-hover:text-[#d4af37] transition-colors" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono-accent text-[10px] text-white/30">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl font-light mb-3 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-white/55 text-sm leading-relaxed max-w-md">
                    {s.short}
                  </p>
                  <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono-accent text-[#d4af37] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link to="/services" data-testid={`service-card-link-${s.id}`}>Learn more</Link>
                    <ArrowUpRight className="w-3 h-3" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="py-24 lg:py-32 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">[ 02 ] Vision & Mission</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light max-w-3xl">
              What we're <span className="text-gradient-gold font-semibold">building toward.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            data-testid="home-vision-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="group relative bg-[#0d0d12] border border-[#24242a] hover:border-[#d4af37]/50 p-10 lg:p-14 transition-all overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/8 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center justify-between mb-10">
                <div className="w-14 h-14 border border-[#d4af37]/50 flex items-center justify-center">
                  <Telescope className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
                </div>
                <span className="font-mono-accent text-[10px] text-white/30">01 / Vision</span>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-light mb-6">Our Vision</h3>
              <p className="text-white/70 text-lg leading-relaxed border-l-2 border-[#d4af37] pl-6">
                To be a trusted technology and engineering partner enabling
                organizations across Saudi Arabia and the region to build a
                smarter, more secure and digitally enabled future.
              </p>
            </div>
          </motion.div>

          <motion.div
            data-testid="home-mission-card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="group relative bg-[#0d0d12] border border-[#24242a] hover:border-[#d4af37]/50 p-10 lg:p-14 transition-all overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-bl from-[#0055FF]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="relative">
              <div className="flex items-center justify-between mb-10">
                <div className="w-14 h-14 border border-[#d4af37]/50 flex items-center justify-center">
                  <Target className="w-6 h-6 text-[#d4af37]" strokeWidth={1.5} />
                </div>
                <span className="font-mono-accent text-[10px] text-white/30">02 / Mission</span>
              </div>
              <h3 className="font-display text-3xl lg:text-4xl font-light mb-6">Our Mission</h3>
              <p className="text-white/70 text-lg leading-relaxed border-l-2 border-[#d4af37] pl-6">
                To help organizations translate technology ambition into
                measurable outcomes through AI, digital engineering,
                cybersecurity, cloud, automation and specialized technology
                talent — delivered with local understanding and global
                engineering standards.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-transparent via-[#080810] to-transparent relative">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] relative overflow-hidden grain">
              <img
                src={ABOUT_IMG}
                alt="Notion Minds Systems partnering with regional enterprise leaders"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-start gap-3">
                <Sparkles className="w-4 h-4 text-[#d4af37] mt-0.5 shrink-0" />
                <div className="flex flex-col gap-1">
                  <p className="font-mono-accent text-xs text-white/80">
                    Supporting national transformation agendas across the region
                  </p>
                  <p className="font-mono-accent text-[10px] text-white/50">
                    Saudi Vision 2030 · Oman Vision 2040 · Qatar National Vision 2030
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">[ 03 ] Why Notion Minds</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light leading-[1.1] mb-8">
              Regional context.<br />
              <span className="text-gradient-gold font-semibold">Global engineering rigor.</span>
            </h2>
            <p className="text-white/65 text-lg leading-relaxed mb-6">
              Notion Minds Systems combines regional market understanding with
              enterprise-grade technology and engineering capabilities.
            </p>
            <p className="text-white/60 leading-relaxed mb-6">
              Our practitioners bring experience across cybersecurity, AI, cloud,
              OT, enterprise architecture and technology delivery — helping
              organizations move from strategy to execution with measurable
              accountability.
            </p>
            <p className="text-white/55 leading-relaxed mb-10">
              We understand the requirements of regulated and
              transformation-driven environments, with delivery aligned to
              applicable Saudi regulatory and security frameworks.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1a1a20] border border-[#1a1a20] mb-10" data-testid="why-differentiators">
              {[
                {
                  n: "01",
                  t: "Saudi Market Understanding",
                  d: "Local context, regulatory awareness and enterprise relationships.",
                },
                {
                  n: "02",
                  t: "Engineering-Led Delivery",
                  d: "Practical implementation capability beyond strategy and advisory.",
                },
                {
                  n: "03",
                  t: "Security by Design",
                  d: "Cybersecurity integrated into transformation rather than treated as an afterthought.",
                },
                {
                  n: "04",
                  t: "Flexible Delivery Models",
                  d: "Consulting, project delivery, ODC and managed services.",
                },
              ].map((it) => (
                <div
                  key={it.n}
                  data-testid={`why-diff-${it.n}`}
                  className="group bg-[#050505] hover:bg-[#0d0d14] p-6 transition-colors relative"
                >
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-mono-accent text-[10px] text-[#d4af37]">{it.n}</span>
                    <span className="h-px flex-1 bg-white/10 group-hover:bg-[#d4af37]/40 transition-colors" />
                  </div>
                  <h3 className="font-display text-lg font-light mb-2 group-hover:text-[#d4af37] transition-colors">
                    {it.t}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">{it.d}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="inline-flex items-center gap-2 border-b border-[#d4af37] text-[#d4af37] pb-1 hover:gap-3 transition-all"
            >
              Read our story <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INDUSTRIES TEASER */}
      <section className="py-24 lg:py-32 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div>
            <p className="font-mono-accent text-xs text-[#d4af37] mb-4">[ 04 ] Industries</p>
            <h2 className="font-display text-4xl lg:text-5xl font-light max-w-3xl">
              Industry-aware, <span className="text-gradient-gold font-semibold">never industry-generic.</span>
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1a1a20]">
          {INDUSTRIES.map((ind, i) => (
            <Link
              to="/industries"
              key={ind.id}
              data-testid={`home-industry-${ind.id}`}
              className="group p-10 bg-[#050505] hover:bg-[#0e0e14] transition-colors"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono-accent text-xs text-white/40">
                  0{i + 1}
                </span>
                <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-[#d4af37] group-hover:rotate-12 transition-all" />
              </div>
              <h3 className="font-display text-2xl font-light mb-3 group-hover:text-[#d4af37] transition-colors">
                {ind.name}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">{ind.blurb}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="relative overflow-hidden border border-[#d4af37]/30 p-12 lg:p-20 grid-pattern-fine">
          <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/8 via-transparent to-transparent" />
          <div className="relative max-w-3xl">
            <h2 className="font-display text-4xl lg:text-6xl font-light leading-tight mb-8">
              Have a transformation in mind?
              <br />
              <span className="text-gradient-gold font-semibold">Let's pressure-test it.</span>
            </h2>
            <p className="text-white/65 text-lg mb-10 max-w-2xl">
              A 45-minute strategy session with our partners — no slideware, no
              salescraft. Walk away with a sharper hypothesis and a clear next step.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                data-testid="home-bottom-cta"
                className="inline-flex items-center gap-2 bg-[#d4af37] text-black px-8 py-4 font-semibold hover:bg-[#e5c059] transition-colors"
              >
                Request a session <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presentation/download`}
                data-testid="home-deck-download"
                className="inline-flex items-center gap-2 border border-white/25 text-white px-8 py-4 font-medium hover:border-white/60 hover:bg-white/5 transition-all"
              >
                Download corporate deck
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
